import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail, Lock, User, Eye, EyeOff, Target, TrendingUp, MessageSquare, Compass, CheckCircle, ArrowRight, Info, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { VeeraLogo } from "@/components/VeeraLogo";
import { sanitizeNextPath } from "@/lib/pricingFlow";
import {
  resolveAuthInitialMode,
  resolveAuthRouteRole,
  resolveAuthSignupDestination,
  resolveAuthSignupRole,
  resolveAuthSignupTrialIntent,
  resolveSignupCtaLabel,
  type AuthMode,
} from "@/lib/soloModeAuth";
import { getSessionItem, removeSessionItem, setSessionItem } from "@/utils/safeStorage";
import { logger } from "@/utils/logger";
import { capturePostHogEvent } from "@/utils/posthog";
import {
  beginSignupAnalyticsIntent,
  clearSignupAnalyticsIntent,
  signupAnalyticsEventProperties,
  type SignupAnalyticsProvider,
} from "@/lib/signupAnalytics";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const signupSchema = z.object({
  fullName: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type LoginFormData = z.infer<typeof loginSchema>;
type SignupFormData = z.infer<typeof signupSchema>;
type AuthAudience = "leader" | "coach";

function captureSignupAnalyticsEvent(
  eventName: "signup_started" | "signup_submitted" | "signup_succeeded" | "signup_failed",
  properties: {
    signup_intent_id: string;
    auth_provider: SignupAnalyticsProvider;
    error_code?: string;
    mode?: AuthMode;
    role: AuthAudience;
    trial_account_mode?: string;
  }
) {
  capturePostHogEvent(eventName, properties);
}

const authAudienceContent: Record<AuthAudience, {
  mobileLabel: string;
  tagline: string;
  heroLine: string;
  heroHighlight: string;
  heroBody: string;
  trustMetric: string;
  trustLine: string;
  quote: string;
  quoteByline: string;
  features: Array<{
    icon: LucideIcon;
    title: string;
    description: string;
  }>;
}> = {
  leader: {
    mobileLabel: "For Leaders",
    tagline: "Private Leadership Continuity",
    heroLine: "Let selected moments",
    heroHighlight: "become a record",
    heroBody:
      "Save reflections, selected meetings, and coaching moments in one continuity thread, so you can see strengths, blind spots, and patterns over time.",
    trustMetric: "No scoring",
    trustLine: "No labels. Human coaching stays central.",
    quote: "A private record for noticing how your selected moments and meetings connect over time.",
    quoteByline: "For leader-led reflection",
    features: [
      {
        icon: Target,
        title: "Choose what comes in",
        description: "Save real situations and add the Coach Veera meeting companion only when a meeting matters.",
      },
      {
        icon: Compass,
        title: "Choose a leadership lens",
        description: "Reflect through the perspective that fits your current growth work.",
      },
      {
        icon: TrendingUp,
        title: "Build continuity over time",
        description: "Notice what begins to repeat without turning growth into a grade.",
      },
      {
        icon: MessageSquare,
        title: "Bring it into coaching",
        description: "Use your record with a coach or coaching partner when ready.",
      },
    ],
  },
  coach: {
    mobileLabel: "For Coaches",
    tagline: "Coaching Continuity",
    heroLine: "The session is not the work.",
    heroHighlight: "Continuity is.",
    heroBody:
      "Coach Veera helps clients bring selected moments back to the work, then connects those moments to your philosophy, questions, and language.",
    trustMetric: "No scoring",
    trustLine: "No surveillance. Human coaching stays central.",
    quote: "Coach Veera helps selected meetings and reflections become useful context for the next coaching conversation.",
    quoteByline: "For coach-led continuity",
    features: [
      {
        icon: Compass,
        title: "Carry your lens",
        description: "Keep your philosophy, questions, and language present between sessions.",
      },
      {
        icon: Target,
        title: "Use selected meeting context",
        description: "Invite Coach Veera into selected meetings so the right context can carry forward.",
      },
      {
        icon: TrendingUp,
        title: "See how coaching lands",
        description: "Notice whether your coaching is showing up in the moments clients bring back.",
      },
      {
        icon: MessageSquare,
        title: "Keep coaching central",
        description: "Support reflection while human judgment stays where it belongs.",
      },
    ],
  },
};

export function AuthPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const queryRoleParam = searchParams.get("role") as "leader" | "coach" | null;
  const routeRoleParam = resolveAuthRouteRole(location.pathname);
  const roleParam = routeRoleParam ?? queryRoleParam;
  const modeParam = routeRoleParam ? "signup" : searchParams.get("mode");
  const inviteParam = searchParams.get("invite");
  const promoParam = searchParams.get("promo") ?? searchParams.get("code");
  const safeNextPath = sanitizeNextPath(searchParams.get("next"));
  const postAuthPath = safeNextPath
    ? `/post-auth?next=${encodeURIComponent(safeNextPath)}`
    : "/post-auth";

  const initialMode = resolveAuthInitialMode(roleParam, modeParam);
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, roles, signIn, signUp, signInWithGoogle, signInWithAzure } = useAuth();

  useEffect(() => {
    if (inviteParam) {
      navigate(`/join/${inviteParam}`, { replace: true });
    }
  }, [inviteParam, navigate]);

  useEffect(() => {
    setMode(resolveAuthInitialMode(roleParam, modeParam));
  }, [modeParam, roleParam]);

  useEffect(() => {
    const normalizedPromo = promoParam?.trim();
    if (normalizedPromo) {
      setSessionItem("billingPromoCode", normalizedPromo);
    }
  }, [promoParam]);
  
  // Contextual headings based on role param
  const getLoginHeading = () => {
    if (roleParam === "leader") {
      return "Welcome back, leader";
    }
    if (roleParam === "coach") {
      return "Sign in to your coaching workspace";
    }
    return "Sign in to your coaching workspace";
  };
  
  const getLoginSubheading = () => {
    if (roleParam === "leader") {
      return "Sign in with the same email your coach invited. If you already use Veera in another role, that same account can add leader access.";
    }
    if (roleParam === "coach") {
      return "Access your leaders and review recent moments";
    }
    return "Access your leaders and review recent moments";
  };

  const roleNextPath = roleParam === "coach" ? "/coach/overview" : roleParam === "leader" ? "/today" : undefined;
  const signupRole = resolveAuthSignupRole(roleParam);
  const signupDestination = resolveAuthSignupDestination(roleParam);
  const signupTrialIntent = resolveAuthSignupTrialIntent(roleParam);
  const audienceContent = authAudienceContent[signupRole];

  const clearPendingSignupState = () => {
    removeSessionItem("signupRole");
    removeSessionItem("signupAccountMode");
    clearSignupAnalyticsIntent();
  };

  useEffect(() => {
    if (mode !== "signup") return;

    const intent = beginSignupAnalyticsIntent({
      role: signupRole,
      authProvider: "email",
      trialAccountMode: signupTrialIntent.accountMode,
    });
    captureSignupAnalyticsEvent("signup_started", {
      ...signupAnalyticsEventProperties(intent),
      mode,
    });
  }, [mode, signupRole, signupTrialIntent.accountMode]);

  // Redirect based on role if already logged in (and NOT actively submitting/navigating)
  useEffect(() => {
    // Only redirect if user has roles AND we're not in the middle of a form submission
    if (user && roles.length > 0 && !isLoading) {
      logger.debug("[AuthPage] User already authenticated with roles, redirecting to /post-auth");
      navigate(postAuthPath, { replace: true });
    }
  }, [user, roles, navigate, isLoading, postAuthPath]);

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const forgotForm = useForm<{ email: string }>({
    resolver: zodResolver(z.object({ email: z.string().email("Please enter a valid email address") })),
    defaultValues: { email: "" },
  });

  const signupForm = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: { fullName: "", email: "", password: "", confirmPassword: "" },
  });

  const handleLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    clearPendingSignupState();
    const { error } = await signIn(data.email, data.password);

    if (error) {
      setIsLoading(false);
      toast({
        title: "Login failed",
        description: error.message === "Invalid login credentials"
          ? roleParam === "leader"
            ? "We couldn't find a matching account. Use the same email your coach invited, or create your leader account first."
            : "Invalid email or password. Please try again."
          : error.message,
        variant: "destructive",
      });
      return;
    }

    // SUCCESS: Navigate IMMEDIATELY to post-auth for role resolution and routing
    // Do NOT wait for useAuth to update state - navigate right away
    logger.debug("[AuthPage] Login succeeded, navigating to /post-auth");
    navigate(postAuthPath, { replace: true });
    // Note: Don't set isLoading to false - we're leaving the page
  };

  const handleForgotPassword = async (data: { email: string }) => {
    setIsLoading(true);
    const redirectUrl = `${window.location.origin}/reset-password`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
      redirectTo: redirectUrl,
    });
    
    setIsLoading(false);
    
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    
    setResetEmailSent(true);
    toast({
      title: "Check your email",
      description: "We've sent you a password reset link.",
    });
  };

  const handleSignup = async (data: SignupFormData) => {
    setIsLoading(true);
    const signupIntent = beginSignupAnalyticsIntent({
      role: signupRole,
      authProvider: "email",
      trialAccountMode: signupTrialIntent.accountMode,
    });
    const signupAnalyticsProperties = signupAnalyticsEventProperties(signupIntent);

    captureSignupAnalyticsEvent("signup_submitted", signupAnalyticsProperties);

    // Store role BEFORE signup so it's available regardless of outcome
    setSessionItem("signupRole", signupRole);
    if (signupTrialIntent.accountMode) {
      setSessionItem("signupAccountMode", signupTrialIntent.accountMode);
    } else {
      removeSessionItem("signupAccountMode");
    }

    const { error } = await signUp(data.email, data.password, data.fullName, signupRole, signupTrialIntent);
    
    if (error) {
      setIsLoading(false);
      captureSignupAnalyticsEvent("signup_failed", {
        ...signupAnalyticsProperties,
        error_code: error.message.includes("already registered") ? "already_registered" : "signup_error",
      });
      // Clear the signup role on error
      removeSessionItem("signupRole");
      removeSessionItem("signupAccountMode");
      
      const message = error.message.includes("already registered")
        ? "This email is already registered. Try logging in instead."
        : error.message;
      
      toast({
        title: "Signup failed",
        description: message,
        variant: "destructive",
      });
      return;
    }

    // SUCCESS: Auth user was created.
    // Email confirmation is disabled in Supabase, so the user should be auto-logged in.
    // Navigate to /post-auth which will wait for the session and handle routing.
    logger.debug("[AuthPage] Signup succeeded, navigating to /post-auth");
    captureSignupAnalyticsEvent("signup_succeeded", signupAnalyticsProperties);
    
    // Small delay to allow Supabase to establish the session
    await new Promise(r => setTimeout(r, 300));
    
    navigate(postAuthPath, { replace: true });
    // Note: Don't set isLoading to false - we're leaving the page
  };

  // Beta helper text for OAuth notice
  const betaAuthNotice = (
    <p className="text-xs text-center text-muted-foreground">
      For beta, sign in with email and password. More options coming soon.
    </p>
  );

  // Role assignment is now handled by /post-signup page
  // This effect only handles redirect for already logged-in users with pending signup
  useEffect(() => {
    const handlePendingSignup = async () => {
      if (!user) return;
      
      const signupRole = getSessionItem("signupRole");
      if (signupRole) {
        // User has a pending signup - redirect to post-signup to complete it
        navigate("/post-signup");
      }
    };

    handlePendingSignup();
  }, [user, navigate]);

  return (
    <>
      <Helmet>
        <title>{mode === "login" ? "Sign In" : resolveSignupCtaLabel(roleParam, false)} | VEERA</title>
        <meta name="description" content="VEERA preserves leadership moments between sessions so leaders and coaches can reflect with more context." />
      </Helmet>

      <div className="min-h-screen bg-[#fbf8f3] text-[#251837] flex dark:bg-[#100c17] dark:text-[#f2edf7]">
        {/* Left Side - Hero Section */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden border-r border-violet-950/25 bg-[radial-gradient(circle_at_78%_18%,rgba(181,113,63,0.22),transparent_28%),radial-gradient(circle_at_68%_72%,rgba(55,177,162,0.16),transparent_34%),linear-gradient(135deg,#0e0a21_0%,#1a0f34_52%,#2a174d_100%)] text-white dark:border-[#d8c7ae]/16">
          <div className="pointer-events-none absolute inset-0 opacity-65" aria-hidden="true">
            <div className="absolute -right-32 top-8 h-[34rem] w-[34rem] rounded-full border border-[#b5713f]/15" />
            <div className="absolute -right-16 top-24 h-[25rem] w-[25rem] rounded-full border border-[#b5713f]/10" />
            <div className="absolute bottom-0 left-1/3 h-px w-2/3 bg-gradient-to-r from-transparent via-[#37b1a2]/40 to-transparent" />
          </div>
          
          <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
            {/* Logo & Tagline */}
            <div>
              <div className="flex items-center gap-3 mb-2 [&_.text-foreground]:!text-white">
                <VeeraLogo size="lg" />
              </div>
              <p className="text-caption font-semibold uppercase tracking-[0.28em] text-[#d99b67]">{audienceContent.tagline}</p>
            </div>

            {/* Main Hero Content */}
            <div className="space-y-8 max-w-xl">
              <div className="space-y-4">
                <h1 className="font-serif text-[3.2rem] leading-[0.95] text-white xl:text-[4.75rem]">
                  {audienceContent.heroLine}
                  <span className="block text-[#d99b67]">{audienceContent.heroHighlight}</span>
                </h1>
                <p className="max-w-2xl text-lg leading-8 text-violet-100/88">
                  {audienceContent.heroBody}
                </p>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-4">
                {audienceContent.features.map((feature) => (
                  <div key={feature.title} className="flex items-start gap-3 rounded-lg border border-white/12 bg-white/[0.055] p-3 shadow-[0_18px_60px_rgba(0,0,0,0.18)] backdrop-blur-sm">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#d99b67]/14">
                      <feature.icon className="h-5 w-5 text-[#f1c19a]" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white text-sm">{feature.title}</h3>
                      <p className="text-xs text-violet-100/72 mt-0.5">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-8 w-8 rounded-full bg-[#ead9c4] border-2 border-[#fbf8f3] flex items-center justify-center dark:border-[#100c17] dark:bg-[#d8c7ae]/14">
                      <span className="text-xs font-medium text-muted-foreground">{String.fromCharCode(64 + i)}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-violet-100/85">
                  <span className="font-semibold text-white">{audienceContent.trustMetric}</span>{" "}
                  {audienceContent.trustLine}
                </p>
              </div>
              
              <div className="rounded-lg border border-white/12 bg-white/[0.055] p-4 backdrop-blur-sm">
                <p className="text-sm italic text-violet-100/78">"{audienceContent.quote}"</p>
                <p className="text-sm font-medium text-white mt-2">{audienceContent.quoteByline}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Auth Form */}
        <div className="w-full lg:w-1/2 xl:w-2/5 flex flex-col">
          {/* Mobile Header */}
          <div className="lg:hidden p-6 pb-0">
            <div className="flex items-center gap-3">
              <VeeraLogo size="md" />
              <div>
                <p className="text-xs text-muted-foreground">{audienceContent.mobileLabel}</p>
              </div>
            </div>
            {mode === "signup" && (
              <div className="mt-8 space-y-4">
                <p className="text-caption font-semibold uppercase tracking-[0.24em] text-[#9b5d34] dark:text-[#d99b67]">
                  {audienceContent.tagline}
                </p>
                <h1 className="font-serif text-[2.55rem] leading-[0.98] text-[#241330] dark:text-white">
                  {audienceContent.heroLine}
                  <span className="block text-[#b5713f] dark:text-[#d99b67]">{audienceContent.heroHighlight}</span>
                </h1>
                <p className="text-base leading-7 text-muted-foreground">{audienceContent.heroBody}</p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">{audienceContent.trustMetric}</span>{" "}
                  {audienceContent.trustLine}
                </p>
              </div>
            )}
          </div>

          <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
            <Card className="w-full max-w-md border-0 bg-transparent shadow-none lg:border-[#d8c7ae]/70 lg:bg-[#fffdf7]/86 lg:shadow-[0_28px_80px_-52px_rgba(76,54,28,0.72)] dark:lg:border-[#d8c7ae]/16 dark:lg:bg-[#18131d]/88">
              <CardContent className="p-0 lg:p-6">
                <div className="space-y-6">
                  {/* Header */}
                  <div className="text-center lg:text-left space-y-2">
                    <h2 className="text-2xl font-bold text-foreground">
                      {mode === "login"
                        ? getLoginHeading()
                        : signupRole === "leader"
                          ? "Create your leader account"
                          : "Create your coach account"}
                    </h2>
                    <p className="text-muted-foreground">
                      {mode === "login"
                        ? getLoginSubheading()
                        : signupRole === "leader"
                          ? "Save selected moments and add the Coach Veera meeting companion when a meeting matters."
                          : "Build better continuity and see how your coaching shows up between sessions."}
                    </p>
                  </div>

                  {/* Leader info notice */}
                  {mode === "signup" && signupRole === "coach" && (
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50 border border-border">
                      <Info className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                      <p className="text-sm text-muted-foreground">
                        Leaders join Veera through your invitation, then choose which meetings and moments belong in the record.
                      </p>
                    </div>
                  )}

                  {/* Login Form */}
                  {mode === "login" && (
                    <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="email"
                            type="email"
                            placeholder="you@example.com"
                            className="pl-10 h-12"
                            {...loginForm.register("email")}
                          />
                        </div>
                        {loginForm.formState.errors.email && (
                          <p className="text-sm text-destructive">
                            {loginForm.formState.errors.email.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="password">Password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••"
                            className="pl-10 pr-10 h-12"
                            {...loginForm.register("password")}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        {loginForm.formState.errors.password && (
                          <p className="text-sm text-destructive">
                            {loginForm.formState.errors.password.message}
                          </p>
                        )}
                      </div>

                      <Button type="submit" className="w-full h-12" disabled={isLoading}>
                        {isLoading ? "Signing in..." : "Sign In"}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>

                      <button
                        type="button"
                        onClick={() => setMode("forgot")}
                        className="w-full text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        Forgot your password?
                      </button>

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">Or</span>
                        </div>
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-12"
                        disabled={isLoading}
                        onClick={async () => {
                          setIsLoading(true);
                          clearPendingSignupState();
                          const { error } = await signInWithGoogle(roleNextPath);
                          if (error) {
                            setIsLoading(false);
                            toast({ title: "Google sign-in failed", description: error.message, variant: "destructive" });
                          }
                        }}
                      >
                        <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                        Continue with Google
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-12"
                        disabled={isLoading}
                        onClick={async () => {
                          setIsLoading(true);
                          clearPendingSignupState();
                          const { error } = await signInWithAzure(roleNextPath);
                          if (error) {
                            setIsLoading(false);
                            toast({ title: "Microsoft sign-in failed", description: error.message, variant: "destructive" });
                          }
                        }}
                      >
                        <svg className="mr-2 h-5 w-5" viewBox="0 0 21 21">
                          <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                          <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                          <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                          <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                        </svg>
                        Continue with Microsoft
                      </Button>
                    </form>
                  )}

                  {/* Forgot Password Form */}
                  {mode === "forgot" && (
                    <div className="space-y-4">
                      {resetEmailSent ? (
                        <div className="text-center space-y-4">
                          <div className="flex justify-center">
                            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                              <CheckCircle className="h-8 w-8 text-primary" />
                            </div>
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground">Check your email</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              We've sent a password reset link to your email address.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => {
                              setMode("login");
                              setResetEmailSent(false);
                            }}
                          >
                            Back to Sign In
                          </Button>
                        </div>
                      ) : (
                        <form onSubmit={forgotForm.handleSubmit(handleForgotPassword)} className="space-y-4">
                          <p className="text-sm text-muted-foreground">
                            Enter your email address and we'll send you a link to reset your password.
                          </p>
                          
                          <div className="space-y-2">
                            <Label htmlFor="forgot-email">Email</Label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input
                                id="forgot-email"
                                type="email"
                                placeholder="you@example.com"
                                className="pl-10 h-12"
                                {...forgotForm.register("email")}
                              />
                            </div>
                            {forgotForm.formState.errors.email && (
                              <p className="text-sm text-destructive">
                                {forgotForm.formState.errors.email.message}
                              </p>
                            )}
                          </div>

                          <Button type="submit" className="w-full h-12" disabled={isLoading}>
                            {isLoading ? "Sending..." : "Send Reset Link"}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>

                          <button
                            type="button"
                            onClick={() => setMode("login")}
                            className="w-full text-sm text-muted-foreground hover:text-primary transition-colors"
                          >
                            Back to Sign In
                          </button>
                        </form>
                      )}
                    </div>
                  )}

                  {/* Signup Form */}
                  {mode === "signup" && (
                    <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="fullName">Full Name</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="fullName"
                            type="text"
                            placeholder="Jane Smith"
                            className="pl-10 h-12"
                            {...signupForm.register("fullName")}
                          />
                        </div>
                        {signupForm.formState.errors.fullName && (
                          <p className="text-sm text-destructive">
                            {signupForm.formState.errors.fullName.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="signup-email">Email</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="signup-email"
                            type="email"
                            placeholder="you@example.com"
                            className="pl-10 h-12"
                            {...signupForm.register("email")}
                          />
                        </div>
                        {signupForm.formState.errors.email && (
                          <p className="text-sm text-destructive">
                            {signupForm.formState.errors.email.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="signup-password">Password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="signup-password"
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••"
                            className="pl-10 pr-10 h-12"
                            {...signupForm.register("password")}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        {signupForm.formState.errors.password && (
                          <p className="text-sm text-destructive">
                            {signupForm.formState.errors.password.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword">Confirm Password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="confirmPassword"
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••"
                            className="pl-10 h-12"
                            {...signupForm.register("confirmPassword")}
                          />
                        </div>
                        {signupForm.formState.errors.confirmPassword && (
                          <p className="text-sm text-destructive">
                            {signupForm.formState.errors.confirmPassword.message}
                          </p>
                        )}
                      </div>

                      <Button type="submit" className="w-full h-12" disabled={isLoading}>
                        {resolveSignupCtaLabel(roleParam, isLoading)}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">Or</span>
                        </div>
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-12"
                        disabled={isLoading}
                        onClick={async () => {
                          setIsLoading(true);
                          const signupIntentOptions = {
                            role: signupRole,
                            ...(signupTrialIntent.accountMode ? { accountMode: signupTrialIntent.accountMode } : {}),
                          };
                          const signupIntent = beginSignupAnalyticsIntent({
                            role: signupRole,
                            authProvider: "google",
                            trialAccountMode: signupTrialIntent.accountMode,
                          });
                          const signupAnalyticsProperties = signupAnalyticsEventProperties(signupIntent);

                          captureSignupAnalyticsEvent("signup_submitted", signupAnalyticsProperties);
                          setSessionItem("signupRole", signupRole);
                          if (signupTrialIntent.accountMode) {
                            setSessionItem("signupAccountMode", signupTrialIntent.accountMode);
                          } else {
                            removeSessionItem("signupAccountMode");
                          }
                          const { error } = await signInWithGoogle(signupDestination, {
                            forceConsent: true,
                            signupIntent: signupIntentOptions,
                          });
                          if (error) {
                            setIsLoading(false);
                            captureSignupAnalyticsEvent("signup_failed", {
                              ...signupAnalyticsProperties,
                              error_code: "oauth_init_error",
                            });
                            clearPendingSignupState();
                            toast({ title: "Google sign-up failed", description: error.message, variant: "destructive" });
                          }
                        }}
                      >
                        <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                        Sign up with Google
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-12"
                        disabled={isLoading}
                        onClick={async () => {
                          setIsLoading(true);
                          const signupIntentOptions = {
                            role: signupRole,
                            ...(signupTrialIntent.accountMode ? { accountMode: signupTrialIntent.accountMode } : {}),
                          };
                          const signupIntent = beginSignupAnalyticsIntent({
                            role: signupRole,
                            authProvider: "microsoft",
                            trialAccountMode: signupTrialIntent.accountMode,
                          });
                          const signupAnalyticsProperties = signupAnalyticsEventProperties(signupIntent);

                          captureSignupAnalyticsEvent("signup_submitted", signupAnalyticsProperties);
                          setSessionItem("signupRole", signupRole);
                          if (signupTrialIntent.accountMode) {
                            setSessionItem("signupAccountMode", signupTrialIntent.accountMode);
                          } else {
                            removeSessionItem("signupAccountMode");
                          }
                          const { error } = await signInWithAzure(signupDestination, {
                            signupIntent: signupIntentOptions,
                          });
                          if (error) {
                            setIsLoading(false);
                            captureSignupAnalyticsEvent("signup_failed", {
                              ...signupAnalyticsProperties,
                              error_code: "oauth_init_error",
                            });
                            clearPendingSignupState();
                            toast({ title: "Microsoft sign-up failed", description: error.message, variant: "destructive" });
                          }
                        }}
                      >
                        <svg className="mr-2 h-5 w-5" viewBox="0 0 21 21">
                          <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                          <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                          <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                          <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                        </svg>
                        Sign up with Microsoft
                      </Button>

                      <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <p className="text-muted-foreground">
                          <span className="font-medium text-foreground">Free to start.</span>{" "}
                          {signupRole === "leader"
                            ? "Save selected moments and add the Coach Veera meeting companion when a meeting matters."
                            : "Invite leaders, build continuity, and see what carries into real work."}
                        </p>
                      </div>
                    </form>
                  )}

                  {/* Terms */}

                  {/* Toggle mode */}
                  <p className="text-center text-sm text-muted-foreground">
                    {mode === "login" ? (
                      <>
                        New to VEERA?{" "}
                        <button
                          type="button"
                          onClick={() => setMode("signup")}
                          className="text-primary hover:underline font-medium"
                        >
                          Create an account
                        </button>
                      </>
                    ) : (
                      <>
                        Already have an account?{" "}
                        <button
                          type="button"
                          onClick={() => setMode("login")}
                          className="text-primary hover:underline font-medium"
                        >
                          Sign in
                        </button>
                      </>
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Footer */}
          <div className="p-6 pt-0 text-center">
            <button
              onClick={() => navigate("/")}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back to home
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
